<script setup>
import { useLayout } from '@/layout/common/composables/layout';
import { cva } from 'class-variance-authority';
import { computed, inject, onMounted, ref, watch } from 'vue';
const props = defineProps({
    class: {
        type: String,
        default: ''
    },
    mode: {
        type: String,
        default: null
    }
});
const { calculatedMenuMode } = useLayout();
const sidebarMenuRef = ref(null);
const menuHeight = ref('auto');
const calculatedVariant = computed(() => {
    if (props.mode) {
        return props.mode;
    }
    return calculatedMenuMode.value;
});
const menuContext = inject('menuContext', null);

const updateHeight = () => {
    if (!menuContext?.collapsible) return;

    if (menuContext.isOpen.value) {
        menuHeight.value = `${sidebarMenuRef.value.scrollHeight + 4}px`;
    } else {
        menuHeight.value = '0px';
    }
};

const menuStyle = computed(() => {
    if (!menuContext?.collapsible || calculatedVariant.value === 'slim' || calculatedVariant.value === 'compact') return {};

    return {
        maxHeight: menuHeight.value,
        overflow: 'hidden',
        transition: 'max-height 0.2s ease-in-out, opacity 0.25s ease-in-out, transform 0.25s ease-in-out',
        opacity: menuContext?.isOpen.value ? 1 : 0,
        transform: menuContext?.isOpen.value ? 'translateY(0)' : 'translateY(-10px)'
    };
});

onMounted(() => {
    if (menuContext?.collapsible && calculatedVariant.value !== 'slim' && calculatedVariant.value !== 'compact') {
        updateHeight();
    }
});

watch(
    () => menuContext?.isOpen.value,
    () => {
        if (menuContext?.collapsible && calculatedVariant.value !== 'slim' && calculatedVariant.value !== 'compact') {
            updateHeight();
        }
    }
);

const sidebarMenuVariants = cva('gap-1 flex flex-col group-has-[[data-sidebar=menu]]/menu-item:pl-6 transition-all', {
    variants: {
        variant: {
            horizontal: 'flex-row'
        }
    }
});

const sidebarMenuClass = computed(() => {
    return sidebarMenuVariants({ variant: calculatedVariant.value, class: props.class });
});
</script>

<template>
    <ul ref="sidebarMenuRef" data-sidebar="menu" :style="menuStyle" :class="sidebarMenuClass">
        <div v-if="menuContext?.collapsible" class="mt-1 gap-1 flex" :class="calculatedVariant === 'horizontal' ? 'flex-row' : 'flex-col'">
            <slot />
        </div>
        <div v-else class="flex gap-1" :class="calculatedVariant === 'horizontal' ? 'flex-row' : 'flex-col'">
            <slot />
        </div>
    </ul>
</template>
